// The review of a returned contract (module 10 U26, screens/2026-09-18-conor-call/ContractReturn.html
// "The page it opens"; module 04 D216, B390, B391, B394; milestone 10 check 11; B429).
//
// Mounted on the item page under the item's own card, for every mail: it asks the API for the
// reviews of that mail's attachments that the signed-in person may read and draws nothing when there
// are none (a mail with no review and a review they may not read look the same, R5). For each:
//
//   1. The decision (U12): the returned file against the version it answers, how that version was
//      found, and what they can do: draft the reply (the existing draft lane, which carries the
//      review's proposed points into its input, Open 73, B481), or take the matter to their own Claude. Taking
//      it, handing it on and parking are the item card's, just above.
//   2. What they changed: the exact comparison, clause by clause or paragraph by paragraph, the
//      words as sent struck through and the words as returned, each change marked tracked or not
//      tracked (B387), and Cobrain's verdict and reasoning with chips that open at the passage.
//   3. Said on calls, not in the document.
//   4. What I would propose.
//   5. The footer: who can see it, and what Cobrain did, including what the one call cost (D62).
//
// A weak match asks "Which document does this answer?" with the choices this reader can open; one
// press answers it and the page compares at once. Nothing on the page counts what the reading was
// not given.

import { CardFrame, Chip, SectionHeading } from '../parts/primitives.js';
import { ActionButton } from '../parts/ActionButton.js';
import { useEffect, useState } from 'react';
import { ApiError, Unauthenticated } from '../api.js';
import { formatDate } from '../labels.js';
import { review, type ChipView, type ReviewPageView, type ReviewView, type UnitView, type Verdict } from './api.js';
import './review.css';

const VERDICT: Record<Verdict, { word: string; cls: string }> = {
  accept: { word: 'Accept', cls: 'ok' },
  your_call: { word: 'Your call', cls: 'push' },
  against_what_was_said: { word: 'Against what was said', cls: 'bad' },
};
const HOW: Record<string, string> = { thread: 'found on the same thread', organisation: 'found among what was sent to them', contracts: 'found in the contracts folder' };

function EvidenceChip({ c }: { c: ChipView }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="rv-chip-wrap">
      <ActionButton reading type="button" className="rv-ev" aria-expanded={open} onClick={() => setOpen((o) => !o)}>{c.label}</ActionButton>
      {open ? <span className="rv-passage" role="note">“{c.text}”</span> : null}
    </span>
  );
}

function Words({ u }: { u: UnitView }) {
  if (u.kind === 'removed') return <>Removed entirely: <span className="rv-was">{u.oldText}</span></>;
  if (u.kind === 'added') return <>Added: <span className="rv-new">{u.newText}</span></>;
  return (
    <>
      {(u.diff ?? []).map((d, i) => d.op === 'equal'
        ? <span key={i}>{d.text} </span>
        : d.op === 'delete'
          ? <span key={i} className={`rv-was${d.tracked === false ? ' rv-quiet' : ''}`}>{d.text} </span>
          : <span key={i} className={`rv-new${d.tracked === false ? ' rv-quiet' : ''}`}>{d.text} </span>)}
    </>
  );
}

function Changed({ v }: { v: ReviewView }) {
  const c = v.comparison!;
  const verdicts = new Map((v.reading?.changes ?? []).map((x) => [x.unitIndex, x]));
  const n = (c.counts.changed ?? 0) + (c.counts.removed ?? 0) + (c.counts.added ?? 0);
  return (
    <CardFrame variant="panel" className="rv-panel">
      <h2>What they changed <span className="rv-sub">{n} change{n === 1 ? '' : 's'} · exact, from the two files{(c.counts.notTracked ?? 0) > 0 ? ` · ${c.counts.notTracked} made without tracked changes` : ''}</span></h2>
      {c.units.length === 0 ? <p className="note">Word for word the version sent.</p> : null}
      {c.units.map((u) => {
        const r = verdicts.get(u.index);
        return (
          <div key={u.index} className="rv-row" data-unit={u.label}>
            <div className="rv-clause">{u.label}</div>
            <div>
              <Words u={u} />
              <div className="rv-marks">
                {u.tracking === 'not_tracked' ? <Chip className="rv-untracked">Not tracked</Chip> : u.tracking === 'tracked' ? <Chip>Tracked{u.trackedBy[0]?.author ? ` · ${u.trackedBy[0].author}` : ''}</Chip> : null}
                {u.moved ? <Chip>Moved</Chip> : null}
                {u.renumbered ? <Chip>Renumbered</Chip> : null}
              </div>
              {r ? (
                <div className="rv-read"><span className={`rv-verdict ${VERDICT[r.verdict].cls}`}>{VERDICT[r.verdict].word}</span>{r.reasoning}
                  {r.evidence.length > 0 ? <div className="rv-chips">{r.evidence.map((e) => <EvidenceChip key={e.id} c={e} />)}</div> : null}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </CardFrame>
  );
}

function One({ v, store, onDraft, onAnswered }: { v: ReviewView; store: string; onDraft?: (() => void) | undefined; onAnswered: () => void }) {
  const [said, setSaid] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const who = v.organisation ?? v.returned.from ?? 'They';

  if (v.state === 'weak' && v.question) {
    return (
      <section className="rv" aria-label={`The review of ${v.returned.name}`}>
        <CardFrame variant="panel">
          <div className="meta row"><Chip className="reg">Contract returned</Chip>{v.organisation ? <Chip>{v.organisation}</Chip> : null}<span className="when">{formatDate(v.returned.arrivedAt)}</span></div>
          <p className="head">{who} sent {v.returned.name} back</p>
          <p className="gist">It matches more than one document we sent. Say which it answers, and Cobrain compares them word for word.</p>
          <p className="q">{v.question.text}</p>
          <div className="opts">
            {v.question.choices.map((c, i) => (
              <ActionButton key={c.candidateId} type="button" className={i === 0 ? 'opt lead' : 'opt'} disabled={busy}
                      onClick={() => { setBusy(true); setSaid(null); void review.answer(store, v.id, c.candidateId).then(() => onAnswered())
                        .catch((e: unknown) => { if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; } setSaid(e instanceof ApiError ? e.message : String(e)); })
                        .finally(() => setBusy(false)); }}>
                {c.name ?? 'Untitled document'} <small>{c.by ? `${c.by}, ` : ''}{formatDate(c.at)} · {Math.round(c.score * 100)}% alike</small>
              </ActionButton>
            ))}
          </div>
          {said ? <p className="error">{said}</p> : null}
        </CardFrame>
        <Footer v={v} />
      </section>
    );
  }
  if (!v.answers || !v.comparison) return null;
  const draftable = !!onDraft && !!v.reading && v.reading.proposals.length > 0;
  const prompt = `Read everything on this matter, then review ${v.returned.name} (returned by ${who}) against ${v.answers.name} and the calls, and give me a markup with the points Cobrain proposes.`;
  return (
    <section className="rv" aria-label={`The review of ${v.returned.name}`}>
      <CardFrame variant="panel">
        <div className="meta row"><Chip className="reg">Contract returned</Chip>{v.organisation ? <Chip>{v.organisation}</Chip> : null}<span className="when">{formatDate(v.returned.arrivedAt)}</span></div>
        <p className="head">{v.returned.name} against {v.answers.name}</p>
        <p className="gist">
          The version {v.answers.by ? `${v.answers.by} ` : ''}sent on {formatDate(v.answers.at)}, {v.answers.chosenBy ? `chosen by ${v.answers.chosenBy}` : HOW[v.answers.source] ?? 'found'}.
          {' '}Compared word for word by Cobrain{v.reading ? ', then read against what you can read on this matter.' : '.'}
        </p>
        <p className="q">What do you want to do?</p>
        <div className="opts">
          {onDraft ? <ActionButton type="button" className="opt lead" disabled={!draftable} onClick={() => onDraft()}>Draft the reply <small>{draftable ? 'with the points below, in your voice; you send it' : 'once Cobrain has proposed points'}</small></ActionButton> : null}
          <ActionButton type="button" className="opt" onClick={() => { void navigator.clipboard?.writeText(prompt).then(() => setSaid('Copied: paste it into your own Claude, where the matter is one question away.')).catch(() => setSaid(prompt)); }}>
            Work on it in my Claude <small>the matter is one question away</small>
          </ActionButton>
        </div>
        {said ? <p className="note rv-said">{said}</p> : null}
      </CardFrame>

      <Changed v={v} />

      {v.reading && v.reading.said.length > 0 ? (
        <CardFrame variant="panel" className="rv-panel">
          <h2>Said on calls, not in the document <span className="rv-sub">{v.reading.said.length}</span></h2>
          {v.reading.said.map((s, i) => (
            <div key={i} className="rv-row"><div className="rv-clause">{s.topic}</div><div>{s.line}
              <div className="rv-read">Nothing in the returned document covers it.</div>
              <div className="rv-chips">{s.evidence.map((e) => <EvidenceChip key={e.id} c={e} />)}</div></div></div>
          ))}
        </CardFrame>
      ) : null}

      {v.reading && v.reading.proposals.length > 0 ? (
        <CardFrame variant="panel" className="rv-panel">
          <h2>What I would propose</h2>
          <ul className="rv-proposals">
            {v.reading.proposals.map((p, i) => <li key={i}>{p.label ? <b>{p.label} </b> : null}{p.line}{p.evidence.length > 0 ? <span className="rv-chips">{p.evidence.map((e) => <EvidenceChip key={e.id} c={e} />)}</span> : null}</li>)}
          </ul>
        </CardFrame>
      ) : null}

      <Footer v={v} />
    </section>
  );
}

function Footer({ v }: { v: ReviewView }) {
  return <p className="foot rv-foot"><b>Who can see this:</b> {v.footer.who} <b>What Cobrain did:</b> {v.footer.did.join(' ')}</p>;
}

/** The reviews of one mail, for the item page. Draws nothing when there is none this person may read. */
export function ReviewPanel({ store, signalId, onDraft }: { store: string; signalId: string; onDraft?: (() => void) | undefined }) {
  const [view, setView] = useState<ReviewPageView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let live = true;
    review.ofSignal(store, signalId)
      .then((v) => { if (live) { setView(v); setError(null); } })
      .catch((e: unknown) => { if (!live) return; if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; } setError(e instanceof ApiError ? e.message : String(e)); });
    return () => { live = false; };
  }, [store, signalId, tick]);
  if (error) return <p className="note">The review could not be read just now. {error}</p>;
  if (!view || view.reviews.length === 0) return null;
  return (
    <div className="rv-all">
      <SectionHeading title={<>The returned document</>} note={<>compared word for word by Cobrain · read against what you can read on the matter</>} />
      {view.reviews.map((v) => <One key={v.id} v={v} store={store} onDraft={onDraft} onAnswered={() => setTick((t) => t + 1)} />)}
    </div>
  );
}
